<template>
  <div
    v-if="displayLocaleSwitcher"
    v-click-outside="closeLocaleSwitcher"
    class="nav-desktop-locale-switcher"
    :class="{ 'nav-desktop-locale-switcher--active': open }"
  >
    <button
      class="nav-desktop-locale-switcher__active"
      @click="toggleLocaleSwitcher"
    >
      <NavFlagIcon :iso-code="activeLocaleObject.locale" />

      <span>{{ activeLocaleObject.locale }}</span>

      <SvgIconArrowMenu />
    </button>

    <div class="nav-desktop-locale-switcher__list-container">
      <div class="nav-desktop-locale-switcher__list-wrapper">
        <div class="nav-desktop-locale-switcher__list-header">
          <span>{{ translation.localeSwitcherHeader }}</span>
        </div>

        <ul class="nav-desktop-locale-switcher__list">
          <li
            v-for="locale in localeNavigation"
            :key="locale.locale"
            class="nav-desktop-locale-switcher__list-item"
            @click="displayLink(locale) ? handleLinkClick() : null"
          >
            <component
              :is="displayLink(locale) ? 't3-link' : 'div'"
              :to="displayLink(locale) ? locale.localePath : null"
              class="nav-desktop-locale-switcher__list-link"
              :class="{ 'nav-desktop-locale-switcher__list-link--disabled': !locale.available }"
            >
              <NavFlagIcon :iso-code="locale.locale" />

              <span>{{ locale.language }}</span>

              <SvgIconCheck v-if="locale.active" />
            </component>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import SvgIconArrowMenu from '~ui/assets/icons/icon-arrow-menu.svg'
import SvgIconCheck from '~ui/assets/icons/icon-check.svg'
import type { ILocaleNavigation } from '~ui/types'

import NavFlagIcon from '../NavComponents/NavFlagIcon.vue'

interface Props {
  localeNavigation: Array<ILocaleNavigation>
  open: boolean
  translation: Record<string, string>
}

const props = defineProps<Props>()

const displayLocaleSwitcher = computed(() => {
  return props.localeNavigation.length > 1
})

const activeLocaleObject = computed(() => {
  return props.localeNavigation.find(({ active }) => active)!
})

const emit = defineEmits<{
  (event: 'link-click'): void
  (event: 'toggle', bool: boolean): void
}>()

const toggleLocaleSwitcher = () => emit('toggle', !props.open)
const closeLocaleSwitcher = () => emit('toggle', false)

const handleLinkClick = () => {
  closeLocaleSwitcher()
  emit('link-click')
}

const displayLink = ({ active, available }: ILocaleNavigation) => {
  return !(active || !available)
}
</script>

<style lang="scss">
.nav-desktop-locale-switcher {
  $base: &;

  &__active {
    display: flex;
    align-items: center;
    gap: rem(4px);
    height: 100%;
    background-color: transparent;
    color: color(grey-800);
    border-radius: em(4px);
    border: 1px solid color(grey-250);
    padding: em(12px) range-clamp(12px, 16px, 1100px, 1400px);
    font-size: rem(16px);
    font-family: inherit;
    text-transform: uppercase;
    white-space: nowrap;
    user-select: none;
    cursor: pointer;
    transition: border-color 0.2s;

    @include media-query(max 1100px) {
      & .nav-flag-icon {
        display: none;
      }
    }

    & .nav-flag-icon {
      margin-right: rem(4px);
    }

    & > svg {
      opacity: 0.8;
      height: 24px;
      width: 24px;
      transition: rotate 0.4s;

      & path {
        fill: currentColor;
      }
    }
  }

  &--active &__active {
    border-color: color(grey-350);

    & svg {
      rotate: 180deg;
    }
  }

  &__list {
    &-header {
      display: flex;
      justify-content: space-between;
      font-weight: 600;
      font-size: rem(16px);
      color: color(dark-blue-1000);
      padding-inline: rem($container-padding);
      padding-block: calc(rem($container-padding) * 1)
        calc(rem($container-padding) / 2);
    }

    &-item {
      transition: background-color 0.2s;

      & span {
        color: color(dark-blue-900);
      }

      &:hover {
        background-color: color(grey-150);
      }
    }

    &-link {
      padding: rem($container-padding);
      display: flex;
      align-items: center;
      gap: rem(12px);
      outline-offset: -4px;

      &:is(a):is(:hover, :active, :focus) {
        color: color(primary);
      }

      & svg {
        margin-left: auto;

        & path {
          fill: color(primary);
        }
      }

      &--disabled {
        opacity: 0.3;
        cursor: not-allowed;
      }
    }
  }

  &__list-container {
    display: grid;
    grid-template-rows: 0fr;
    align-content: start;
    position: absolute;
    z-index: z-index(over - 1);
    top: calc(48px /* $featured-navigation-h */ + calc(15px /* $header-gap */ / 2));
    inset-inline: 0;
    z-index: z-index(over);
    visibility: hidden;
    overflow: hidden;
    background-color: color(white);
    border-radius: em(8px);
    border: 1px solid color(grey-250);
    transition:
      top 0.2s,
      grid-template-rows 0.2s,
      visibility 0.2s;
  }

  &__list-wrapper {
    min-height: 0;
  }

  &--active &__list-container {
    grid-template-rows: 1fr;
    visibility: visible;
  }

  .header--themed &__active {
    color: color(white);
    border-color: color(dark-blue-850);
  }

  .header--themed &--active &__active {
    color: color(white);
    border-color: color(dark-blue-650);
  }
}
</style>
